import { each as asyncEach, map as asyncMap } from "async";
import * as storeUtils from "../services/distributedStore/storeUtils";
import Logger from "./logger";
import { StandardCallback, StandardError, StandardErrorCallback, StandardPromise } from "../types";
import { IRouterClient } from "./routerClient";

export type ListenerFunction<T = any> = (err: StandardError, response: { field: string; value: T }) => void;

export type DispatchListenerFunction = (any) => void;

export type FieldOnlyParam = {
	field: string;
};

export type ListenerParam = {
	field: string;
	listener: ListenerFunction;
};
export type RemoveListenersType = ListenerParam | ListenerParam[];

export type Mappings = Record<string, any>;

export type RemoveStoreCallback = (
	params: {
		store: string;
		global?: boolean;
	},
	cb?: StandardErrorCallback<boolean>
) => StandardPromise<boolean>;
export class StoreModel {
	/**
	 * @ignore
	 */
	routerClient: IRouterClient;

	/**
	 * @ignore
	 */
	removeStoreCallback: RemoveStoreCallback;

	/**
	 * @ignore
	 */
	isGlobal: boolean = false;

	/**
	 * @ignore
	 */
	values: Record<string, any> = {};

	/**
	 * @ignore
	 */
	listeners: Record<string, ListenerFunction[]> = {};

	/**
	 * @ignore
	 * @deprecated
	 */
	registeredDispatchListeners: DispatchListenerFunction[] = [];

	/**
	 * @ignore
	 */
	mapping: Mappings = {};

	/**
	 * Prevents multiple listener requests on the same field from being sent to the DistributedStoreService
	 * @ignore
	 */
	subs: Record<string, boolean> = {};

	/**
	 * @ignore
	 */
	name: string;

	/**
	 * @ignore
	 */
	constructor(
		params: {
			global?: boolean;
			name: string;
			values?: Record<string, any>;
		},
		routerClient: IRouterClient,
		removeStoreCallback: RemoveStoreCallback
	) {
		this.routerClient = routerClient;
		this.removeStoreCallback = removeStoreCallback;
		this.isGlobal = !!params.global;
		// params.store is for backward compatibility. See notes in distributedStoreService.ts
		this.name = params.name ?? (params as any).store ?? "StoreModel() invariant. store.name not provided.";
		if (params.values) this.values = params.values;

		storeUtils.initObject(this.values, null, this.mapping);

		// Add listeners for global stores. Not needed for local stores as everything happens internally.
		if (this.isGlobal) {
			this.routerClient.addListener(`storeService.dispatch.${this.name}`, (err, response) => {
				this.handleDispatchedMessages(err, response);
			});
		}
	}

	/** This is the Flux dispatcher. It can be used dispatch actions across stores. These action are not caught inside of the global store service. For more information, you can read the [Flux documentation](https://facebook.github.io/flux/docs/overview.html).
	 *
	 * Example:
	 * ```
	 * store.Dispatcher.register(function(action){
	 * 	if(action.actionType === "ACTION1") {
	 * 		// Do something with the action here.
	 * 	}
	 * })
	 * ```
	 * @ignore
	 */
	Dispatcher = {
		register: (fn: (update: any) => void) => {
			this.registeredDispatchListeners.push(fn);
		},
		dispatch: (data: any) => {
			if (this.isGlobal) {
				this.routerClient.transmit(`storeService.dispatch.${this.name}`, data);
			} else {
				this.handleDispatchedMessages(null, { data });
			}
		},
	};

	/**
	 * @param {*} err
	 * @param {*} message
	 * @ignore
	 */
	handleDispatchedMessages(err: StandardError, message?: { data: any }) {
		if (message?.data) {
			this.registeredDispatchListeners.forEach((listener) => {
				listener(message.data);
			});
		}
	}

	/**
	 * Set a value in the store. Two events will be triggered with topics of: store and field.
	 *
	 * ```javascript
	 * store.setValue({ field:'field1', value:"new value" });
	 * ```
	 * @param {String} params.field The name of the field where data will be stored
	 * @param {String} params.value Value to be stored
	 * @param {function} cb callback
	 */
	setValue(params: { field: string; value: any }, cb?: StandardErrorCallback<void>): void {
		if (!params.field) {
			Logger.system.error("DistributedStore.setValue:no field provided", params);
		}
		if (!params.hasOwnProperty("value")) {
			Logger.system.error("DistributedStore.setValue:no value provided", params);
		}
		if (this.isGlobal) {
			const data = {
				store: this.name,
				field: params.field,
				value: params.value,
			};
			this.routerClient.query("storeService.setValue", data, (err) => {
				if (cb) cb(err);
			});
		} else {
			const removals = storeUtils.checkForObjectChange(this.values, params.field, this.mapping);

			storeUtils.setPath(this.values, params.field, params.value);

			storeUtils.mapField(this.values, params.field, this.mapping);

			if (removals) {
				this.sendRemovals(removals);
			}

			this.triggerListeners(this.name, this);
			this.publishObjectUpdates(params.field, this.mapping);
			if (cb) cb(null);
		}
	}

	/**
	 * Handles changes to the store. Will publish from the field that was changed and back.
	 * @private Do not call this function directly. It will be converted to private member in a future release
	 * @ignore
	 */
	publishObjectUpdates(startField: string, mappings: Mappings) {
		const currentMapping = mappings;
		while (startField) {
			this.triggerListeners(`${this.name}.${startField}`, storeUtils.byString(this.values, startField));
			startField = currentMapping[startField];
		}
	}

	/**
	 * Send items that are no longer mapped or had their map change. If a value is remapped we'll send out the new value.
	 * @private Do not call this function directly. It will be converted to private member in a future release
	 * @ignore
	 */
	sendRemovals(removals: string[]) {
		for (let removal in removals) {
			this.triggerListeners(`${this.name}.${removal}`, storeUtils.byString(this.values, removal));
		}
	}

	/**
	 * This will set multiple values in the store.
	 *
	 * ```javascript
	 * store.setValues([{ field:'field1', value:"new value" }]);
	 * ```
	 * @param {function} cb callback
	 * @param {Array<{ field : string, value : any}>} fields An array where each element is like the object below.
	 */
	setValues(fields: { field: string; value: any }[], cb?: StandardErrorCallback<void>): void {
		if (!fields) {
			return Logger.system.error("DistributedStore.setValues:no params given");
		}

		if (!Array.isArray(fields)) {
			return Logger.system.error("DistributedStore.setValues:params must be an array");
		}

		asyncEach(
			fields,
			(field, done) => {
				this.setValue(field, done);
			},
			(err) => (cb ? cb(err) : null)
		);
	}

	/**
	 * Get a value from the store. If global is not set, we'll check local first then we'll check global. Returns the value of the field. If no callback is given and the value is local, this will run synchronously.
	 *
	 * Be very careful not to `await` this function as it is not promisified. Use `await getGlobalValue` instead.
	 *
	 * ```javascript
	 * store.getValue({ field: 'field1' }, function(err,value){});
	 * store.getValue('field1', function(err,value){});
	 * ```
	 * @param {object|string} params The field where the value is stored.
	 * @param {String} params.field The field where the value is stored.
	 * @param {StandardCallback} cb Will return the value if found.
	 * @returns any Don't use the return statement. Always use the cb.
	 */
	getValue<T = any>(params: { field: string } | string, cb?: StandardErrorCallback<T>): T | any {
		if (typeof params === "string") {
			params = { field: params };
		}
		if (!params.field) {
			cb?.("no field provided");
			return "no field provided";
		}

		if (this.isGlobal) {
			this.getGlobalValue(params.field, cb);
		} else {
			const fieldValue = storeUtils.byString(this.values, params.field);
			if (fieldValue !== undefined) {
				cb?.(null, fieldValue as any); // Allow converstion from storeUtils to the requested type
				return fieldValue as T;
			} else {
				cb?.("couldn't find a value");
				return null;
			}
		}
		return null;
	}

	/**
	 * Get the entire store.
	 *
	 * @param { Function} cb Will return the value if found
	 * @returns object | void - The store in key/value form if it's a local store - otherwise void
	 * @ignore
	 */
	getAll(cb: StandardErrorCallback<Record<string, any>>): any {
		if (this.isGlobal) {
			this.getGlobalValues(null, cb);
		} else {
			if (cb) cb(null, this.values);
			return this.values;
		}
	}

	/**
	 * Get multiple values from the store. Returns an object of with the fields as keys.If no callback is given and the value is local, this will run synchronously. Returns an object of with the fields as keys.If no callback is given and the value is local, this will run synchronous
	 *
	 * ```javascript
	 * store.getValues([{ field:'field1' }, { field:'field2' }], function(err,values){});
	 * store.getValues(['field1', 'field2'], function(err,values){});
	 * ```
	 * @param {Array.<object>|Array.<String>} fields An Array of field objects. If there are no fields provided, or value is null, all values in the store are returned.
	 * @param {string} fields.field The field where the value is stored.
	 * @param {Function} [cb] Will return the value if found.
	 * @returns {Object} - returns an object of with the fields as keys.If no callback is given and the value is local, this will run synchronous
	 */
	getValues(fields: { field: string }[] | string[], cb?: StandardErrorCallback<any>): any {
		// If fields is null, treat it as though no fields property was passed.
		if (fields === null) {
			fields = cb as any;
		}

		if (typeof fields === "function") {
			cb = fields as StandardCallback;
			if (this.isGlobal) {
				return this.getGlobalValues(null, cb);
			}

			if (!cb) {
				return this.values;
			}
			return cb(null, this.values);
		}

		// If the user sent a single value instead of an array (violating the typing rules for this function)
		if (!Array.isArray(fields)) {
			const result = this.getValue(fields, cb);
			return {
				[fields]: result,
			};
		}

		if (this.isGlobal) {
			return this.getGlobalValues(fields, cb);
		}
		const values = {};

		for (let i = 0; i < fields.length; i++) {
			const item = fields[i];
			const field = typeof item === "string" ? item : item.field;
			const fieldValue = storeUtils.byString(this.values, field);
			values[field] = fieldValue;
		}
		if (!cb) {
			return values;
		}
		return cb(null, values);
	}

	/**
	 * Get a single value from a *global* store. This function is promisifed.
	 *
	 * @param {string} field The field to fetch
	 * @param {(err, data) => void} [cb] Optional callback to receive value. `err` will be string if an error occurred. `data` will contain the returned value or be undefined.
	 * @return {Promise<{err, data}} `err` will be string if an error occurred. `data` will contain the returned value or be undefined.
	 */
	async getGlobalValue<T = any>(field: string, cb?: StandardErrorCallback<T>): StandardPromise<T> {
		const { err, response } = await this.routerClient.query("storeService.getValue", {
			store: this.name,
			field: field,
		});
		cb?.(err, response.data);
		return { err, data: response.data };
	}

	/**
	 * Get values from the global store.
	 * @private Do not call this function directly. It will be converted to private member in a future release
	 * @ignore
	 */
	getGlobalValues(params: { field: string }[] | string[] | null, cb?: StandardErrorCallback<Record<string, any>>) {
		this.routerClient.query(
			"storeService.getValues",
			{
				store: this.name,
				fields: params,
			},
			(err, response) => {
				if (cb) cb(err, response.data);
			}
		);
	}

	/**
	 * Remove a value from the store.
	 *
	 * ```javascript
	 * store.removeValue({ field: 'field1' }, function(err,bool){});
	 * ```
	 * @param {Object | String} params - Either an object (`{ field: string }`) or string
	 * @param {String} param.field The name of the field
	 * @param {Function} cb returns an error if there is one
	 */
	removeValue(
		params:
			| string
			| {
					field: string;
			  },
		cb?: StandardErrorCallback<void>
	) {
		const reconciled = {
			field: typeof params === "string" ? params : params.field,
			value: null,
		};

		return this.setValue(reconciled, cb);
	}

	/**
	 * Removes multiple values from the store.
	 *
	 * ```javascript
	 * store.removeValues([{ field: 'field1' }], function(err,bool){});
	 * ```
	 * @param {Object[] | String[]} params - An Array of field objects
	 * @param {String} params.field - The name of the field
	 * @param {Function} cb -  returns an error if there is one.
	 */
	removeValues(params: string[] | { field: string }[], cb) {
		if (!Array.isArray(params)) {
			return cb("The passed in parameter needs to be an array");
		}
		asyncMap(params, this.removeValue, (err, data) => cb(err, data));
	}

	/**
	 * Destroys the store.
	 * @param {Function} cb Function to be invoked after the store is destroyed.
	 */
	destroy(cb?: StandardErrorCallback<boolean>) {
		const params = {
			store: this.name,
			global: this.isGlobal,
		};

		this.removeStoreCallback(params, (err) => {
			if (err) {
				if (cb) cb(err);
			} else {
				if (cb) cb(null, true);
			}
		});
	}

	/**
	 * Adds a subscription for the desired dynamic store changes
	 * @ignore
	 */
	changeSub(change: string) {
		if (!this.subs[change]) {
			if (this.isGlobal) {
				this.routerClient.subscribe(`storeService${change}`, this.handleChanges);
			}
			this.subs[change] = true;
		}
	}

	/**
	 * Add a listener to the store at either the store or field level. If no field is given, the store level is used. You can also listen to nested object (e.g., field1.nestedField).
	 *
	 * ```javascript
	 * var myFunction = function(err,data) {
	 * }
	 * store.addListener({ field:'field1' }, myFunction, cb);
	 * ```
	 * @param {String} params.field The piece of data that you want to listen on. If this is empty it listens to all changes of the store.
	 * @param {Function} fn the function to call when the data changes
	 * @param {Function} cb callback to be invoked
	 */
	addListener(params: { field?: string }, fn: ListenerFunction, cb?: () => void) {
		// Backward compatibility, addListener for entire store
		if (typeof params === "function") {
			fn = params;
			params = {};
		}
		const { field } = params;

		const fieldName = field ? `.${field}` : "";
		const combined = `${this.name}${fieldName}`;
		this.listeners[combined] = [fn].concat(this.listeners[combined] ?? []);

		this.changeSub(combined);
		cb?.();
	}

	/**
	 * Add an array of listeners as  objects or strings. If using strings, you must provide a function callback.
	 *
	 * ```javascript
	 * var myFunction = function(err,data){
	 * }
	 * store.addListeners([{
	 * 	field: 'field1',
	 * 	listener: myFunction
	 * },
	 * {
	 * 	field:'field2',
	 * 	listener: myFunction
	 * }],
	 * null, cb);
	 * store.addListeners([{ field: 'field1' },{ field: 'field2', listener: myFunction }], myFunction, cb);
	 * store.addListeners(['field1','field2'], myFunction, cb);
	 * ```
	 * @param {String} params.field The piece of data that you want listen on. If this is empty it listen to all changes of the store.
	 * @param {String} params.listener The function to call when the piece of data is modified. If this is empty, fn is used.
	 * @param {function} fn The function to call when the piece of data is modified.
	 * @param {function} cb callback to be invoked when the listeners are added.
	 */
	addListeners(
		params: { field: string; listener: ListenerFunction } | { field: string; listener: ListenerFunction }[] | string[],
		fn: ListenerFunction,
		cb?: () => void
	): void {
		// First overload is a single listener - deprecated
		if (!Array.isArray(params)) {
			Logger.deprecated(
				"distributedStoreClient StoreModel - addListeners() with an object instead of an array is deprecated. Please use addListener() instead."
			);
			this.addListener(params, fn, cb);
			return;
		}

		const overrideFunction = typeof fn === "function" ? fn : null;

		for (let i = 0; i < params.length; i++) {
			const item = params[i];
			let field: string = typeof item === "string" ? item : item.field;
			const ls: ListenerFunction | null = typeof item !== "string" ? item.listener : overrideFunction;

			if (ls) {
				const fieldName = field ? `.${field}` : "";
				const combined = `${this.name}${fieldName}`;
				this.listeners[combined] = [ls].concat(this.listeners[combined] ?? []);
				this.changeSub(combined);
			}
		}
		cb?.();
	}

	/**
	 * Remove a listener from store. If no field is given, we look for a store listener
	 * @param {String} params.field - The data field with the listener that you want to remove.
	 * @param {function} fn The handler passed into `addListener` or `addListeners`.
	 * @param {function} cb returns true if it was successful in removing the listener.
	 *
	 * ```javascript
	 * var myFunction = function(err,data){}
	 * store.removeListener({ field: 'field1' }, MyFunction, function(bool){});
	 * ```
	 */
	removeListener(params: { field?: string }, fn: ListenerFunction, cb?: StandardErrorCallback<boolean>): void {
		const { field } = params;

		const fieldName = field ? `.${field}` : "";
		const combined = `${this.name}${fieldName}`;

		if (this.listeners[combined]) {
			for (let i = 0; i < this.listeners[combined].length; i++) {
				if (this.listeners[combined][i] === fn) {
					this.listeners[combined].splice(i, 1);
					cb?.(null, true);
					return;
				}
			}
		}
		cb?.(null, false);
	}

	/**
	 * Remove an array of listeners from the store
	 * @param {String} params.field The data field with the listener that you want to remove.
	 * @param {String} params.listener The handler passed into `addListener` or `addListeners`.
	 * @param {function} fn The handler passed into `addListener` or `addListeners`.
	 * @param {function} cb returns true if it was successful in removing the listener.
	 *
	 * ```javascript
	 * var myFunction = function(err,data){}
	 * store.removeListeners({ field: 'field1' }, MyFunction, function(bool){});
	 * store.removeListeners([{ field: 'field1', listener: MyFunction}], function(bool){});
	 * store.removeListeners(['field1'], MyFunction, function(bool){});
	 * ```
	 */
	removeListeners(params: RemoveListenersType, fn?: ListenerFunction<any>, cb?: StandardErrorCallback<boolean>): void {
		if (!Array.isArray(params)) {
			if (typeof params === "function") {
				this.removeListener({} as FieldOnlyParam, params, cb);
			} else if (params.field && fn) {
				this.removeListener(params as FieldOnlyParam, fn, cb);
			}
			return cb?.(null, true);
		}

		const overrideFunction = typeof fn === "function" ? fn : null;

		params.forEach((item) => {
			const field: string = typeof item === "string" ? item : item.field;
			const ls: ListenerFunction | null = typeof item !== "string" ? item.listener : overrideFunction;

			const fieldName = field ? `.${field}` : "";
			const combined = `${this.name}${fieldName}`;
			this.listeners[combined] = this.listeners[combined].filter((listener) => listener !== ls);
		});

		cb?.(null, true);
	}

	/**
	 * Handles all changes coming in from the service.
	 * @private Do not call this function directly. It will be converted to private member in a future release
	 * @ignore
	 */
	handleChanges = (
		err: StandardError,
		response: {
			data: {
				store: string;
				storeData: any;
				field: string;
				value: any;
			};
		}
	) => {
		if (err) {
			Logger.system.error("DistributedStoreClient", err);
		} else {
			const { store, field, storeData, value } = response.data;
			if (store) {
				const fieldName = field ? `.${field}` : "";
				const combined = `${this.name}${fieldName}`;
				const val = storeData ? storeData : value;
				this.triggerListeners(combined, val);
			}
		}
	};

	/**
	 * Trigger any function that is listening for changes
	 * @private Do not call this function directly. It will be converted to private member in a future release
	 * @ignore
	 */
	triggerListeners(listenerKey: string, data: any) {
		const listeners = this.listeners[listenerKey] ?? [];
		listeners.forEach((listener) => {
			Logger.system.debug("DistributedStore.triggerListeners", listenerKey, data);
			listener(null, {
				field: listenerKey,
				value: data,
			});
		});
	}
}

export default StoreModel;
